import axios from 'axios';
import DramaboxUtil from './utils.js';

class Dramabox {
  constructor() {
    this.util = new DramaboxUtil();

    // Detect environment properly
    const isDev = import.meta.env.DEV;

    // Configure API URL based on environment
    this.usingPhpProxy = false;
    this.phpProxyMode = 'path'; // 'path' or 'query'

    if (isDev) {
      // Development: use Vite proxy
      this.baseUrl = 'https://sapi.dramaboxdb.com';
    } else {
      // Production: check for PHP proxy URL first, fallback to direct URL
      const phpProxyUrl = import.meta.env.VITE_PHP_PROXY_URL;
      if (phpProxyUrl) {
        // Use PHP proxy (recommended for production)
        this.baseUrl = phpProxyUrl;
        this.usingPhpProxy = true;
        // Detect proxy mode: if URL ends with '=' it's query mode
        this.phpProxyMode = phpProxyUrl.includes('?') ? 'query' : 'path';
        console.log('[Dramabox] Using PHP proxy:', phpProxyUrl, `(${this.phpProxyMode} mode)`);
      } else {
        // Fallback: direct API access (may have CORS issues)
        this.baseUrl = 'https://sapi.dramaboxdb.com';
        console.warn('[Dramabox] Using direct API access. CORS errors may occur.');
      }
    }
    this.webficUrl = 'https://www.webfic.com';
    this.tokenCache = null;
    this.tokenData = null;
    this.http = axios.create({
      timeout: 30000
    });
  }

  async getToken(timestamp = Date.now(), retryCount = 0) {
    // Prevent infinite loop
    if (retryCount > 3) {
      console.error('[ERROR] Max retry attempts reached for getToken');
      throw new Error('Failed to get token after 3 attempts');
    }

    // Check localStorage first for existing token
    const storedToken = typeof localStorage !== 'undefined' ? localStorage.getItem('dramabox_token') : null;
    if (storedToken) {
      try {
        const tokenData = JSON.parse(storedToken);
        // ✅ REVERTED: Token valid for 24 hours (same as apiyangbenar)
        const tokenAge = Date.now() - tokenData.timestamp;
        if (tokenAge < 24 * 60 * 60 * 1000) { // 24 hours ✅
          console.log('[Dramabox] Using cached token (age:', Math.floor(tokenAge/3600000), 'hours)');
          this.tokenData = tokenData;
          return tokenData;
        } else {
          console.log('[Dramabox] Token expired (>24h), fetching new one');
          if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('dramabox_token');
          }
        }
      } catch (error) {
        console.error('[ERROR] Failed to parse stored token:', error);
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem('dramabox_token');
        }
      }
    }

    // Check in-memory token
    if (this.tokenData) {
      const tokenData = this.tokenData;
      const tokenAge = Date.now() - tokenData.timestamp;
      if (tokenAge < 24 * 60 * 60 * 1000) { // 24 hours ✅
        console.log('[Dramabox] Using memory token (age:', Math.floor(tokenAge/3600000), 'hours)');
        return tokenData;
      }
    }

    try {
      const deviceId = await this.util.generateUUID();
      const androidId = await this.util.randomAndroidId();
      const spoffer = await this.util.generateRandomIP();

      const headers = {
        "tn": ``,
        "version": "470",
        "vn": "4.7.0",
        "cid": "DAUAF1064291",
        "package-Name": "com.storymatrix.drama",
        "Apn": "1",
        "device-id": deviceId,
        "language": "in",
        "current-Language": "in",
        "p": "48",
        "Time-Zone": "+0700",
        "md": "Redmi Note 8",
        "ov": "9",
        'over-flow': 'new-fly',
        "android-id": androidId,
        "mf": "XIAOMI",
        "brand": "Xiaomi",
        "Content-Type": "application/json; charset=UTF-8",
      };

      const body = JSON.stringify({ distinctId: null });
      const signString = `timestamp=${timestamp}${body}${deviceId}${androidId}`;
      const signature = await this.util.sign(signString);

      if (!signature) {
        console.error('[ERROR] Failed to generate signature');
        throw new Error('Signature generation failed');
      }

      headers['sn'] = signature;

      const url = `${this.baseUrl}/drama-box/ap001/bootstrap?timestamp=${timestamp}`;
      console.log('[Dramabox] Fetching NEW token from server...');
      console.log('[Dramabox] DeviceID:', deviceId);
      console.log('[Dramabox] AndroidID:', androidId);

      const res = await axios.post(url, { distinctId: null }, { headers });

      if (!res.data?.data?.user) {
        console.error('[ERROR] Invalid response, no user data:', res.data);
        console.error('[ERROR] Full response:', res.data);
        return await this.getToken(Date.now(), retryCount + 1);
      }

      const tokenData = {
        token: res.data.data.user.token,
        deviceId,
        androidId,
        spoffer,
        uuid: res.data.data.user.uid,
        attributionPubParam: res.data.data.attributionPubParam,
        timestamp: Date.now(),
      };

      console.log('[Dramabox] ✅ NEW token generated successfully');
      console.log('[Dramabox] Token:', tokenData.token.substring(0, 20) + '...');

      // Save to localStorage
      if (typeof localStorage !== 'undefined') {
        try {
          localStorage.setItem('dramabox_token', JSON.stringify(tokenData));
          console.log('[Dramabox] Token saved to localStorage');
        } catch (error) {
          console.error('[ERROR] Failed to save token to localStorage:', error);
        }
      }

      // Save to memory
      this.tokenData = tokenData;
      //console.log('[DEBUG] New token generated and cached');
      return tokenData;
    } catch (error) {
      console.error("[ERROR] Failed to get token:", error);
      throw error;
    }
  }

  buildHeaders(tokenData, timestamp) {
    return {
      "tn": `Bearer ${tokenData.token}`,
      "version": "470",
      "vn": "4.7.0",
      "cid": "DAUAF1064291",
      "package-Name": "com.storymatrix.drama",
      "Apn": "1",
      "device-id": tokenData.deviceId,
      "language": "in",
      "current-Language": "in",
      "p": "48",
      "Time-Zone": "+0700",
      "md": "Redmi Note 8",
      "ov": "9",
      'over-flow': 'new-fly',
      "android-id": tokenData.androidId,
      "mf": "XIAOMI",
      "user-id": tokenData.uuid,
      "brand": "Xiaomi",
      "Content-Type": "application/json; charset=UTF-8",
    };
  }

  async request(endpoint, payload = {}, isWebfic = false, method = "POST") {
    try {
      const timestamp = Date.now();
      let url, headers;

      if (isWebfic) {
        url = `${this.webficUrl}${endpoint}`;
        headers = {
          "Content-Type": "application/json",
          "pline": "DRAMABOX",
          "language": "in",
        };
      } else {
        const tokenData = await this.getToken(timestamp);
        url = `${this.baseUrl}${endpoint}?timestamp=${timestamp}`;
        headers = this.buildHeaders(tokenData, timestamp);

        const body = JSON.stringify(payload);
        headers['sn'] = await this.util.sign(`timestamp=${timestamp}${body}${tokenData.deviceId}${tokenData.androidId}${headers['tn']}`);
      }

      const config = {
        method: method.toUpperCase(),
        url,
        headers,
        data: method.toUpperCase() !== "GET" ? payload : undefined,
      };

      const response = await this.http.request(config);
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(`HTTP ${error.response.status}`);
      }
      throw error;
    }
  }

  // Drama Detail Methods
  async getDramaDetail(bookId, needRecommend = false, from = "book_album") {
    if (!bookId) {
      throw new Error("bookId is required!");
    }

    return await this.request("/drama-box/chapterv2/detail", {
      needRecommend,
      from,
      bookId
    });
  }

  async getDramaDetailV2(bookId) {
    const data = await this.request(`/webfic/book/detail/v2?id=${bookId}&language=in`, {
      id: bookId,
      language: 'in'
    }, true, 'GET');
    const { chapterList, book } = data?.data || {};
    const chapters = [];
    chapterList.forEach((ch, i) => {
      //episode index = ch.index
      //episode name = ch.name
      //episode id = ch.id
      chapters.push({ index: ch.index, id: ch.id });
    });

    return { drama: book, chapters };
  }

  // Chapter Methods
  async getChapters(bookId, quality = 720, eps) {
    console.log('[Dramabox] getChapters called:', { bookId, quality, eps });
    
    const data = await this.request("/drama-box/chapterv2/batch/load", {
      boundaryIndex: 0,
      index: eps,
      currencyPlaySource: "discover_175_rec",
      currencyPlaySourceName: "首页发现_Untukmu_推荐列表",
      preLoad: false,
      rid: "",
      pullCid: "",
      loadDirection: 1,
      startUpKey: "",
      bookId
    });

    console.log('[Dramabox] API Response:', {
      success: !!data,
      hasData: !!data?.data,
      hasChapterList: !!data?.data?.chapterList,
      chapterCount: data?.data?.chapterList?.length || 0,
      rawData: data
    });

    const chapters = data?.data?.chapterList || [];
    const result = [];
    chapters.forEach((ch, i) => {
      const eps = ch.chapterName.replace(/\s+/g, '-');
      const cdn = ch.cdnList;
      const videoPath = cdn?.flatMap(cdn => cdn.videoPathList)?.find(v => v.quality === quality)?.videoPath || "N/A";
      
      console.log(`[Dramabox] Chapter ${i + 1}:`, {
        name: ch.chapterName,
        hasCdn: !!cdn,
        cdnCount: cdn?.length || 0,
        videoPath: videoPath.substring(0, 50) + '...'
      });
      
      result.push({ eps, videoPath });
    });

    console.log('[Dramabox] getChapters result:', {
      resultCount: result.length,
      firstVideo: result[0]
    });

    return result;
  }

  async batchDownload(bookId, chapterIdList) {
    if (!bookId || !chapterIdList || chapterIdList.length === 0) {
      throw new Error("bookId and chapterIdList are required!");
    }

    const data = await this.request("/drama-box/chapterv2/batchDownload", {
      bookId,
      chapterIdList
    });
    const chList = data?.data?.chapterVoList || [];
    const result = [];
    chList.forEach(ch => {
      const eps = ch.chapterName.replace(/\s+/g, '-');
      const cdn = ch.cdnList?.find(c => c.isDefault === 1);
      const videoPath = cdn?.videoPathList?.find(v => v.isDefault === 1)?.videoPath || "N/A";
      result.push({ eps, videoPath });
    });
    //console.log(result)
    return result;
  }

  async getDramaList(pageNo = 1, pageSize = 10) {
    const data = await this.request("/drama-box/he001/classify", {
      typeList: (pageNo == 1) ? [] : [{ type: 1, value: "" }, { type: 2, value: "" }, { type: 3, value: "" }, { type: 4, value: "" }, { type: 4, value: "" }, { type: 5, value: "1" }],
      showLabels: false,
      pageNo: pageNo.toString(),
      pageSize: pageSize.toString()
    });

    const rawList = data?.data?.classifyBookList?.records || [];
    const isMore = data?.data?.classifyBookList?.isMore || 0;
    const list = rawList.flatMap(item => {
      if (item.cardType === 3 && item.tagCardVo?.tagBooks) {
        return item.tagCardVo.tagBooks;
      }
      return [item];
    });

    const uniqueList = list.filter(
      (v, i, arr) => arr.findIndex(b => b.bookId === v.bookId) === i
    );

    const result = uniqueList.map(book => {
      return {
        id: book.bookId,
        name: book.bookName,
        cover: book.coverWap,
        chapterCount: book.chapterCount,
        introduction: book.introduction,
        tags: book.tagV3s,
        playCount: book.playCount,
      };
    });

    return { isMore: isMore == 1, book: result };
  }

  async getFilterList(pageNo = 1, pageSize = 30) {
    const data = await this.request('/drama-box/he001/classify', {
      typeList: [],
      showLabels: true,
      pageNo,
      pageSize,
    });
    let result = data?.data?.classifyList || [];
    //console.log(result)
    const mapped = {
      region: [],
      sound: [],
      vip: [],
      tag1: [],
      tag2: [],
      sort: []
    };

    let tagIndex = 1;

    result.forEach(item => {
      switch (item.type) {
        case 1:
          mapped.region = item.showList;
          break;
        case 2:
          mapped.sound = item.showList;
          break;
        case 3:
          mapped.vip = item.showList;
          break;
        case 4:
          if (tagIndex === 1) {
            mapped.tag1 = item.showList;
            tagIndex++;
          } else {
            mapped.tag2 = item.showList;
          }
          break;
        case 5:
          mapped.sort = item.showList;
          break;
      }
    });
    return mapped;
  }

  async getBookFromFilter(pageNo = 1, pageSize = 20, filters = { region: "", sound: "", vip: "", tag1: "", tag2: "", sort: "1" }) {
    const typeList = [
      { type: 1, value: filters.region || "" },
      { type: 2, value: filters.sound || "" },
      { type: 3, value: filters.vip || "" },
      { type: 4, value: filters.tag1 || "" },
      { type: 4, value: filters.tag2 || "" },
      { type: 5, value: filters.sort || "1" }
    ];
    const data = await this.request("/drama-box/he001/classify", {
      typeList,
      showLabels: false,
      pageNo,
      pageSize,
    });
    let result = data?.data?.classifyBookList?.records || [];
    const isMore = data?.data?.classifyBookList.isMore || 0;
    result = result.map(book => {
      return {
        id: book.bookId,
        name: book.bookName,
        cover: book.coverWap,
        chapterCount: book.chapterCount,
        introduction: book.introduction,
        tags: book.tagV3s,
        playCount: book.playCount,
      };
    });
    return { isMore: isMore == 1, book: result };
  }

  async getCategorie(tagId = '1452', pageNo = 1, pageSize = 20, sourceBookId = '41000118948', sortType = 2) {
    const data = await this.request("/drama-box/book/tagPageInfo", {
      sourceScene: 'doubleCard',
      tagId,
      sortType,
      sourceBookId,
      pageNo,
      pageSize
    });
    let result = data?.data?.tagPageList.records || [];
    result = result.map(book => {
      return {
        id: book.bookId,
        name: book.bookName,
        cover: book.coverWap,
        chapterCount: book.chapterCount,
        introduction: book.introduction,
        tags: book.tagV3s,
        playCount: book.playCount,
      };
    });
    return result;
  }

  async getRecommendedBooks(pageNo = 1) {
    const data = await this.request("/drama-box/he001/recommendBook", {
      isNeedRank: 1,
      specialColumnId: 0,
      pageNo
    });

    const rawList = data?.data?.recommendList?.records || [];
    const list = rawList.flatMap(item => {
      if (item.cardType === 3 && item.tagCardVo?.tagBooks) {
        return item.tagCardVo.tagBooks;
      }
      return [item];
    });

    const uniqueList = list.filter(
      (v, i, arr) => arr.findIndex(b => b.bookId === v.bookId) === i
    );

    const result = uniqueList.map(book => {
      return {
        id: book.bookId,
        name: book.bookName,
        cover: book.coverWap,
        chapterCount: book.chapterCount,
        introduction: book.introduction,
        tags: book.tagV3s,
        playCount: book.playCount,
      };
    });

    return result;
  }

  async searchDrama(keyword, pageNo = 1, pageSize = 20) {
    const data = await this.request("/drama-box/search/search", {
      searchSource: '搜索按钮',
      pageNo,
      pageSize,
      from: 'search_sug',
      keyword
    });
    let result = data?.data?.searchList || [];
    const isMore = data?.data?.isMore;
    result = result.map(book => {
      return {
        id: book.bookId,
        name: book.bookName,
        cover: book.cover,
        introduction: book.introduction,
        tags: book.tagNames,
        playCount: book.playCount,
      };
    });
    return { isMore: isMore == 1, book: result };
  }

  async searchDramaIndex() {
    const data = await this.request("/drama-box/search/index");
    const raw = data?.data?.hotVideoList || [];
    const result = raw.map(book => {
      return {
        id: book.bookId,
        name: book.bookName,
        cover: book.cover,
        introduction: book.introduction,
        tags: book.tagNames,
        playCount: book.hotCode,
      };
    });
    return result;
  }
}

export default new Dramabox();
